import React, { useEffect, useState } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { ArrowRight } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useAutoGrowInput } from '../../hooks/useAutoGrowInput';
import { textInputRows } from '../../utils/webStyles';
import { Button } from '../common';

/** Roughly four lines of body text before the input scrolls instead. */
const MAX_INPUT_HEIGHT = 96;

interface CommentComposerProps {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  isSending: boolean;
  isEditing: boolean;
  onCancelEdit: () => void;
  /**
   * gorhom scrollables need their own TextInput inside a sheet; the inline
   * presentation uses the plain RN one. Must forward its ref — the composer
   * measures the host node to auto-grow (see useAutoGrowInput).
   */
  inputComponent?: React.ComponentType<
    React.ComponentProps<typeof TextInput> & { ref?: React.Ref<TextInput> }
  >;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * The mockup's "ask bar": one hairline pill holding the input and an ink
 * circular send. Shared by the inline thread and the sheet so a comment is
 * always typed into the same control, never into a lookalike that opens
 * something else.
 */
export function CommentComposer({
  value,
  onChangeText,
  onSend,
  isSending,
  isEditing,
  onCancelEdit,
  inputComponent: Input = TextInput,
  style,
  testID,
}: CommentComposerProps) {
  const { colors } = useTheme();
  const canSend = Boolean(value.trim()) && !isSending;
  const { ref, height, contentHeight, measure, onContentSizeChange } =
    useAutoGrowInput(MAX_INPUT_HEIGHT);
  const [isFocused, setIsFocused] = useState(false);

  // Covers typing, the clear after a send, and entering edit mode with the
  // comment already in the box.
  useEffect(measure, [value, measure]);
  // Past one line, a pill squeezes the text into a capsule: go to a
  // rounded rectangle and drop Send to the bottom, like a chat box (Sept 28).
  const multiline = contentHeight != null && contentHeight > SINGLE_LINE_HEIGHT + 1;

  return (
    <View
      style={[
        styles.bar,
        multiline && styles.barMultiline,
        {
          backgroundColor: colors.background,
          borderColor: isFocused ? colors.borderFocus : colors.border,
        },
        style,
      ]}>
      <Input
        ref={ref}
        style={[styles.input, { color: colors.text }, height != null && { height }]}
        placeholder={isEditing ? 'Edit comment...' : 'Add a comment...'}
        placeholderTextColor={colors.textTertiary}
        value={value}
        onChangeText={onChangeText}
        onContentSizeChange={onContentSizeChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onSubmitEditing={onSend}
        returnKeyType="send"
        blurOnSubmit={false}
        maxLength={500}
        multiline
        // One line at rest; useAutoGrowInput takes it from there.
        {...textInputRows(1)}
        testID={testID ?? 'comments-input'}
      />
      {isEditing && (
        <Button
          title="Cancel"
          onPress={onCancelEdit}
          variant="ghost"
          size="sm"
          style={styles.cancel}
        />
      )}
      <TouchableOpacity
        onPress={onSend}
        disabled={!canSend}
        accessibilityRole="button"
        accessibilityLabel="Send comment"
        accessibilityState={{ disabled: !canSend }}
        testID="comments-send"
        style={[
          styles.send,
          { backgroundColor: canSend ? colors.action : colors.surface },
          !canSend && styles.sendDisabled,
        ]}>
        {isSending ? (
          <ActivityIndicator size="small" color={colors.actionOn} />
        ) : (
          <ArrowRight size={15} color={canSend ? colors.actionOn : colors.textTertiary} />
        )}
      </TouchableOpacity>
    </View>
  );
}

/** The input's height at one line: its minHeight plus its vertical padding. */
const SINGLE_LINE_HEIGHT = TYPOGRAPHY.body.lineHeight + SPACING.xs * 2;

const styles = StyleSheet.create({
  barMultiline: {
    alignItems: 'flex-end',
    borderRadius: BORDER_RADIUS.lg,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingLeft: SPACING.md,
    paddingRight: SPACING.xs,
    paddingVertical: SPACING.xs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.full,
  },
  input: {
    flex: 1,
    ...TYPOGRAPHY.body,
    // A textarea top-aligns its text, so any height beyond the line box shows
    // up as slack underneath it. Keep the minimum at exactly one line and let
    // symmetric padding do the rest: the text is then centred by construction,
    // whatever the type preset is.
    minHeight: TYPOGRAPHY.body.lineHeight,
    paddingVertical: SPACING.xs,
    maxHeight: MAX_INPUT_HEIGHT,
    outlineStyle: 'none' as never,
  },
  cancel: {
    paddingHorizontal: SPACING.sm,
  },
  send: {
    width: 30,
    height: 30,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendDisabled: {
    opacity: 0.9,
  },
});
